/**
 * Invoice money (BE-3d, BE-20a, BE-20b) — real API.
 *
 * Government-fee payments on direct invoice lines, return to draft, tax credit notes, the partner credit-note prompt,
 * and void / remove-line with paid-item outcomes. Backend snake_case ↔ camelCase here.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList, unwrapPage } from '../../utils/apiConvert';

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v !== undefined));
const body = (data) => keysCamelToSnake(clean(data));
const inv = (id) => `/api/invoices/${id}`;
const upload = async (url, file) => {
  const form = new FormData();
  form.append('file', file);
  return unwrapData(await ApiService.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } }));
};
const outcomesBody = (outcomes) => (outcomes && outcomes.length ? outcomes.map((o) => keysCamelToSnake(clean(o))) : undefined);

export const invoiceMoneyService = {
  // ── direct-line government fee (DRAFT only) ───────────────────────────────
  getFeePayments: async (id) => unwrapList(await ApiService.get(`${inv(id)}/fee-payments`)).items,
  recordFee: async (id, lineId, payment) =>
    unwrapData(await ApiService.post(`${inv(id)}/line-items/${lineId}/fee-payments`, body({ component: 'GOVT_FEE', ...payment }))),
  reverseFee: async (id, paymentId, reason) =>
    unwrapData(await ApiService.post(`${inv(id)}/fee-payments/${paymentId}/reverse`, { reason })),
  replaceFee: async (id, paymentId, reason, payment) =>
    unwrapData(await ApiService.post(`${inv(id)}/fee-payments/${paymentId}/replace`,
      { reason, payment: body({ component: 'GOVT_FEE', ...payment }) })),
  setFeeEvidence: async (id, lineId, data) =>
    unwrapData(await ApiService.put(`${inv(id)}/line-items/${lineId}/disbursement`, body({ classification: 'AGENT', ...data }))),
  removeFeeEvidence: async (id, lineId) => unwrapData(await ApiService.delete(`${inv(id)}/line-items/${lineId}/disbursement`)),
  uploadFeeReceipt: async (id, lineId, file) => upload(`${inv(id)}/line-items/${lineId}/disbursement/receipt`, file),
  feeReceiptUrl: async (id, lineId) => unwrapData(await ApiService.get(`${inv(id)}/line-items/${lineId}/disbursement/receipt-url`)),

  // ── FINAL invoices ────────────────────────────────────────────────────────
  returnToDraft: async (id, reason) => unwrapData(await ApiService.post(`${inv(id)}/return-to-draft`, { reason })),
  voidInvoice: async (id, reason, outcomes) =>
    unwrapData(await ApiService.post(`${inv(id)}/void`, { reason, paid_item_outcomes: outcomesBody(outcomes) })),
  deleteInvoice: async (id, outcomes) =>
    ApiService.delete(inv(id), outcomes && outcomes.length ? { data: { paid_item_outcomes: outcomesBody(outcomes) } } : {}),
  removeLine: async (id, lineId, outcomes) =>
    ApiService.delete(`${inv(id)}/line-items/${lineId}`, outcomes && outcomes.length ? { data: { paid_item_outcomes: outcomesBody(outcomes) } } : {}),

  // ── credit notes ──────────────────────────────────────────────────────────
  getCreditNotes: async (id) => unwrapList(await ApiService.get(`${inv(id)}/credit-notes`)).items,
  // lines: [{ invoiceLineItemId, component, amount }]
  issueCreditNote: async (id, reason, lines) =>
    unwrapData(await ApiService.post(`${inv(id)}/credit-notes`, { reason, lines: lines.map((l) => keysCamelToSnake(l)) })),
  openCreditNotePdf: async (id, creditNoteId) => {
    const res = await ApiService.get(`${inv(id)}/credit-notes/${creditNoteId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },

  // ── partner credit notes not yet passed on to the customer ────────────────
  getPartnerPrompts: async (id) => unwrapList(await ApiService.get(`${inv(id)}/partner-credit-prompts`)).items,
  getAllPartnerPrompts: async (page = 0, size = 20) =>
    unwrapPage(await ApiService.get('/api/partner-payments/credit-prompts', { params: { page, size } })),
};

export default invoiceMoneyService;
